import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';

import { Button, Checkbox, Currency, H2, Icon, Spinner } from '../../../components';
import { TranslatedCopy } from '../../../intl/TranslatedCopy';

import { ZERO_DOLLAR_VALUE } from '../../../util/constants';
import { mapSilhouette } from '../../../util/silhouettes';

import { newDepartmentTypes } from '../../../types/allotments';
import { formTypes } from '../../../types/form';

class DepartmentDetails extends Component {
  componentDidUpdate(prevProps) {
    const { department, allotmentForm, change } = this.props;
    if (!department) return;
    if (allotmentForm && department.billable) {
      if (
        !allotmentForm.values ||
        prevProps.department.billable !== department.billable ||
        prevProps.department.departmentNumber !== department.departmentNumber
      ) {
        change('deptBillableOrdersCheckbox', department.billable);
      }
    }
  }

  componentWillUnmount() {
    const { change } = this.props;
    change('deptBillableOrdersCheckbox', '');
  }

  render() {
    const { allotmentForm, department, isAllowedBillable, onSubmit, submitting } = this.props;
    if (!department) return null;
    const {
      allotment,
      currencyCode,
      lockedAllotment,
      purchaseOptions,
      remainingAllotment,
      unitAllotments
    } = department;
    const isBillable = department.billable;

    return (
      <aside className='overviewContent ta-sm-l pb2-sm'>
        <section className='flex-container row start mb8-sm pb8-sm border-bottom'>
          <div className='overviewContent__header bg-white  u-full-width'>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <div style={{ marginRight: '16px', paddingRight: '8px' }}>
                <div className='body-1 u-bold' data-desc='allotments__sidebar_dept-total'>
                  {allotment && (
                    <Currency value={allotment} type={currencyCode} error={allotment < ZERO_DOLLAR_VALUE} />
                  )}
                </div>
                <p className='text-color-secondary body-4'>
                  <TranslatedCopy id='allotments.dashboard.balance' />
                </p>
              </div>
              <div style={{ marginRight: '64px' }}>
                <div className='allotment__total body-1 u-bold' data-desc='allotments__sidebar_dept-remaining'>
                  {remainingAllotment && (
                    <Currency
                      value={remainingAllotment}
                      type={currencyCode}
                      error={remainingAllotment < ZERO_DOLLAR_VALUE}
                    />
                  )}
                </div>
                <p className='text-color-secondary body-4'>
                  <TranslatedCopy id='global.remaining' />
                </p>
              </div>
            </div>
          </div>
        </section>
        {unitAllotments && unitAllotments.length > 0 && (
          <section className='flex-container row start mb8-sm pb8-sm border-bottom'>
            <span className='mr2-sm d-sm-ib va-sm-m'>
              <Icon name='box' alt='unit allotment icon' />
            </span>
            <div className='allotment__options_details d-sm-ib va-sm-t'>
              <H2 className='mb1-sm'>
                <TranslatedCopy id='global.unitAllotment' />
              </H2>
              {unitAllotments.map(option => (
                <div className='mt2-sm'>
                  <div className='body-4 text-color-warm-grey' data-desc='allotments__sidebar_dept-unit-amount'>
                    <TranslatedCopy
                      id='allotments.sidebar.details.unit.details'
                      values={{
                        amount: option.allotmentAmount,
                        // TODO: Figure out a way to make this the human readable description
                        description: mapSilhouette(option)
                      }}
                    />
                  </div>
                  <div className='body-4' data-desc='allotments__sidebar_dept-unit-remaining'>
                    <TranslatedCopy
                      id='allotments.sidebar.details.unit.remaining'
                      values={{ amount: parseInt(option.allotmentAmount, 10) - parseInt(option.totalSpend, 10) }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        {purchaseOptions && purchaseOptions.length > 0 && (
          <section className='flex-container row start mb8-sm pb8-sm border-bottom'>
            <span className='mr2-sm d-sm-ib va-sm-m'>
              <Icon name='filled-dollar' alt='unit allotment icon' />
            </span>
            <div className='allotment__options_details d-sm-ib va-sm-t'>
              <H2>
                <TranslatedCopy id='global.purchaseOptions' />
              </H2>
              {purchaseOptions.map(option => {
                const unitLeftToPurchase =
                  parseInt(option.minimumUnitsRequired, 10) - parseInt(option.totalUnitsPurchased, 10);
                return (
                  <div className='mt2-sm' data-desc='allotments__sidebar_dept-purchaseOptions'>
                    <div className='body-4 text-color-warm-grey'>
                      <TranslatedCopy
                        id='allotments.sidebar.details.purchase.details'
                        values={{
                          get: option.amountBuy,
                          buy: option.amountFree,
                          description: mapSilhouette(option),
                          left: unitLeftToPurchase < 0 ? 0 : unitLeftToPurchase
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
        {lockedAllotment && lockedAllotment !== ZERO_DOLLAR_VALUE && (
          <section className='mb8-sm pb8-sm border-bottom'>
            <span className='mr2-sm d-sm-ib va-sm-m'>
              <Icon name='filled-lock' alt='unit allotment icon' />
            </span>
            <div className='d-sm-ib va-sm-t'>
              <H2 className='mb1-sm'>
                <TranslatedCopy id='allotments.sidebar.details.locked.title' />
              </H2>
              <div className='body-4 text-color-warm-grey' data-desc='allotments__sidebar_dept-locked'>
                <TranslatedCopy id='allotments.sidebar.details.locked.min' />
                <> </>
                <Currency value={lockedAllotment} type={currencyCode} error={lockedAllotment < ZERO_DOLLAR_VALUE} />
              </div>
            </div>
          </section>
        )}
        {isAllowedBillable && (
          <>
            <Field
              name='deptBillableOrdersCheckbox'
              component={Checkbox}
              label={new TranslatedCopy({ id: 'allotments.sidebar.details.billable' }).toString()}
              title={new TranslatedCopy({ id: 'allotments.sidebar.details.billable' }).toString()}
            />
            <Button
              type='submit'
              secondary
              extraClassName='mt5-sm full-width'
              data-desc='currentAllotmentsBillable__submit'
              disabled={
                submitting ||
                (allotmentForm &&
                  allotmentForm.values &&
                  allotmentForm.values.deptBillableOrdersCheckbox === isBillable)
              }
              onClick={onSubmit}
            >
              {submitting ? (
                <Spinner className='relative-container p3-sm' />
              ) : (
                <TranslatedCopy id='global.saveChanges' />
              )}
            </Button>
          </>
        )}
      </aside>
    );
  }
}

DepartmentDetails.propTypes = {
  allotmentForm: PropTypes.shape(formTypes),
  change: PropTypes.func.isRequired,
  department: newDepartmentTypes,
  isAllowedBillable: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired
};

DepartmentDetails.defaultProps = {
  allotmentForm: null,
  department: null
};

export default DepartmentDetails;
